/**
 * mind-map/index.js — the editor.
 *
 * Three decisions shape everything here.
 *
 * The first is that the KEYBOARD is the tool. Every competing mind map is built
 * around dragging boxes, which is why using one feels slower than writing the
 * same list in a text file. Tab makes a child, Enter makes a sibling, typing
 * replaces the label, arrows walk the tree — the same grammar as an outliner,
 * so a map can be built as fast as it can be thought of. The layout is
 * automatic precisely so that no time is spent arranging.
 *
 * The second is that the map and the text are ONE object seen two ways. The
 * outline pane is not an import box: it is the same document, live in both
 * directions. People arrive with notes they already wrote, and they leave with
 * notes they can paste somewhere else.
 *
 * The third is that structure is the only thing worth dragging. Position is
 * computed, so dragging a node cannot move it "somewhere prettier" — but it CAN
 * re-file it under a different parent, which is the single most common thing
 * that happens while a map is being built and the one thing the first version
 * could not do at all.
 */
import * as Layout from './layout.js';
import * as Outline from './outline.js';
import * as Render from './render.js';
import * as Model from './model.js';
import * as Store from './store.js';
import * as Share from './share.js';
import * as Exports from './exports.js';
import * as Present from './present.js';
import * as Org from './org.js';
import * as OrgLayout from './org-layout.js';
import * as OrgRender from './org-render.js';
import { createHistory } from '../image-studio/history.js';

const T = (window.__MM && window.__MM.T) || {};
const CFG = (window.__MM && window.__MM.cfg) || {};
const ASSETS = CFG.assets || {};
const SLUG = 'mind-map';

const $ = (id) => document.getElementById(id);
const t = (k, fallback) => (T[k] === undefined ? (fallback || k) : T[k]);
const stage = (n) => { try { window.__stage && window.__stage(n); } catch (e) {} };
const ae = (s, d) => { try { window.__aeFunnel && window.__aeFunnel(s, d || {}); } catch (e) {} };
const toast = (m, kind) => { try { window.__toast && window.__toast(m, kind || 'info'); } catch (e) {} };

/* ── state ─────────────────────────────────────────────────────────────────── */
let doc = null;
let selected = null;
let laid = null;
let view = { x: 0, y: 0, k: 1 };
let editing = null;
let query = '';
let hits = null;             // Set of ids matching the search
let hitList = [];
let hitAt = -1;
let linkFrom = null;         // "connect two nodes" mode
let presenting = null;
let focusIds = null;         // presentation focus
let textDirty = false;       // the outline pane is the one being typed in
let textStale = false;       // the map moved on while the pane had focus
/* The bar follows the SELECTION, and on arrival the selection is the centre —
 * showing it then would park a toolbar over the middle of a map nobody has
 * touched yet. It appears on the first real pick (click, key, search jump). */
let barArmed = false;

const starterStrings = () => ({
    root: t('starter_root', 'Моя карта'),
    a: t('starter_a', 'Идея'),
    b: t('starter_b', 'Задача'),
    c: t('starter_c', 'Вопрос'),
});

/* ── undo ──────────────────────────────────────────────────────────────────── */
const history = createHistory(
    () => ({ doc: Model.plain(doc), selected }),
    (s) => {
        doc = Model.adopt(JSON.parse(JSON.stringify(s.doc)));
        selected = s.selected;
        persist();
        draw();
        syncText();
    }
);

/* ── persistence ───────────────────────────────────────────────────────────── */
let saveTimer = null;
let saveWarned = false;
function persist() {
    clearTimeout(saveTimer);
    saveTimer = setTimeout(() => {
        const ok = Store.save(doc);
        if (!ok && !saveWarned) {
            // A silent failure here means "the tool lost my map" one reload
            // later. Say it once, and say what still works.
            saveWarned = true;
            toast(t('err_store', 'Не удалось сохранить карту в браузере — скачайте её файлом'), 'warning');
        }
        refreshLibrary();
    }, 400);
}

/* ── drawing ───────────────────────────────────────────────────────────────── */

function visibleCrossLinks() {
    if (!doc.links || !doc.links.length || !laid) return [];
    const shown = Object.create(null);
    for (const n of laid.nodes) shown[n.id] = true;
    return doc.links.filter((l) => shown[l.from] && shown[l.to]);
}

const isOrg = () => !!doc && doc.layout === 'org';

/** What the cards show — chart settings, plus whether the chart names anyone at all. */
function orgOpts() {
    const o = doc.org || Model.plainOrg({});
    return { photos: o.photos, count: o.count, compact: o.compact, named: Org.namesPeople(doc.root), T };
}

function layoutNow() {
    if (isOrg()) {
        const o = orgOpts();
        Org.annotate(doc.root);
        OrgRender.measure(doc.root, o);
        Render.paint(doc.root, { theme: doc.theme });
        return OrgLayout.compute(doc.root, { compact: o.compact });
    }
    Render.measure(doc.root, { shape: doc.shape });
    Render.paint(doc.root, { theme: doc.theme });
    return Layout.compute(doc.root, {
        sides: doc.layout === 'right' ? 'right' : (doc.layout === 'down' ? 'down' : 'both'),
    });
}

function draw() {
    const host = $('mmCanvas');
    if (!host) return;
    laid = layoutNow();

    const svg = Render.buildSvg(laid, {
        pad: 30,
        theme: doc.theme,
        shape: doc.shape,
        crossLinks: visibleCrossLinks(),
        hits,
        focus: focusIds,
    });
    svg.setAttribute('class', 'mm-svg');
    svg.setAttribute('width', '100%');
    svg.setAttribute('height', '100%');
    svg.setAttribute('viewBox', `0 0 ${laid.width + 60} ${laid.height + 60}`);
    svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
    applyView(svg);

    host.textContent = '';
    host.appendChild(svg);
    markSelection();
    updateCounts();
    positionNodeBar();
}

function applyView(svg) {
    const el = svg || $('mmCanvas').querySelector('svg');
    if (!el) return;
    el.style.transform = `translate(${view.x}px,${view.y}px) scale(${view.k})`;
    el.style.transformOrigin = '50% 50%';
}

function markSelection() {
    const host = $('mmCanvas');
    if (!host) return;
    host.querySelectorAll('.mm-node').forEach((g) => {
        const id = g.getAttribute('data-id');
        const on = id === selected;
        g.classList.toggle('on', on);
        g.setAttribute('aria-selected', on ? 'true' : 'false');
        g.classList.toggle('linking', !!linkFrom && id === linkFrom);
    });
}

function updateCounts() {
    const s = Model.stats(doc);
    const set = (id, v) => { const el = $(id); if (el) el.textContent = String(v); };
    set('mmCount', s.nodes);
    set('mmDepth', s.depth);
    const os = $('mmOrgStats');
    if (os) {
        os.hidden = !isOrg();
        if (isOrg()) {
            const sum = Org.summary(doc.root);
            set('mmOrgPeople', OrgRender.fmtNum(sum.named ? sum.people : sum.staff));
            // mmOrgVacantN, not mmOrgVacant: that id is the card panel's checkbox, and
            // writing the count into it left the footer at «Вакансий: 0» (приёмка 29.10)
            set('mmOrgVacantN', OrgRender.fmtNum(sum.vacant));
            set('mmOrgStaff', OrgRender.fmtNum(sum.staff));
            const pl = $('mmOrgPeopleL');
            if (pl) pl.textContent = sum.named ? t('stat_people', 'Людей:') : t('stat_units', 'Штатных единиц:');
            const sw = $('mmOrgStaffW');
            if (sw) sw.hidden = !sum.named;
        }
    }
    const ms = $('mmMapStats');
    if (ms) ms.hidden = isOrg();
    const tasks = $('mmTasks');
    if (tasks) {
        tasks.hidden = !s.tasks;
        const v = $('mmTasksVal');
        if (v) v.textContent = s.done + '/' + s.tasks;
    }
    const name = $('mmName');
    if (name) name.textContent = (doc.root.text || t('untitled', 'Без названия')).slice(0, 40);
    const links = $('mmLinksStat');
    if (links) links.hidden = !s.links;
}

/* ── viewport ──────────────────────────────────────────────────────────────── */

/** How many CSS pixels one drawing unit is worth before our own zoom. */
function baseScale() {
    const host = $('mmCanvas');
    if (!host || !laid) return 0;
    const box = host.getBoundingClientRect();
    if (!box.width || !box.height) return 0;
    return Math.min(box.width / (laid.width + 60), box.height / (laid.height + 60));
}

/**
 * Two different questions, two different answers.
 *
 * «Вписать» means the WHOLE map, because that is what the word promises — it
 * must not quietly stop at part of it. But the view you ARRIVE at cannot be
 * that: the drawing is fitted by preserveAspectRatio, so a sixteen-node map on
 * a phone lands at a scale where a 14px label renders at four — technically
 * the whole map, practically a picture of one. So the opening view is zoomed
 * until type is readable and parked on the centre, which is where a mind map
 * is read from, and the whole-map view is one button away.
 *
 * Both stop at MAX_FIT CSS px per drawing unit. «meet» has no ceiling of its
 * own: the four-node starter map in a 1500 × 560 stage (a wide screen) came out
 * at ×2.9 — a 40 px root and 30 px branches, a poster instead of a map. Past
 * the ceiling the map stays at a size a person reads and edits, in the middle.
 */
const MAX_FIT = 1.4;
// which automatic view is on screen ('open' | 'fit'), until the person zooms or
// pans: the stage changing width (the editor going wide, the outline pane) redoes
// it — a view chosen by hand is never re-timed
let viewAuto = null;

function fit() {
    view = { x: 0, y: 0, k: 1 };
    const base = baseScale();
    if (base > MAX_FIT) view.k = MAX_FIT / base;
    viewAuto = 'fit';
    applyView();
}

function openView() {
    // An org chart opens fitted, like the «Вписать» button: the readable-zoom view
    // below parks the CENTRE on the middle of the stage, and an org chart's centre
    // is its top card — the chart then hung in the lower half with the top empty,
    // and the person went looking for the chart they had just imported (приёмка 29.10).
    if (isOrg()) { fit(); return; }
    view = { x: 0, y: 0, k: 1 };
    viewAuto = 'open';
    const base = baseScale();
    const host = $('mmCanvas');
    if (base > MAX_FIT) {
        view.k = MAX_FIT / base;
    } else if (base > 0 && laid && host) {
        const box = host.getBoundingClientRect();
        const drawW = laid.width + 60, drawH = laid.height + 60;
        const readable = 12 / (Render.STYLE.fontSize * base);
        // …but never so close that the map stops looking like a map. The cap
        // keeps roughly half of it in view, and it adjusts itself: a tall
        // narrow map is limited by its height, a wide one by its width.
        const showAtLeast = Math.min(
            1.9 * box.width / (drawW * base),
            1.9 * box.height / (drawH * base)
        );
        view.k = Math.min(2.6, Math.max(1, Math.min(readable, showAtLeast)));
        const root = laid.nodes[0];
        if (view.k > 1 && root) {
            view.x = (drawW / 2 - (root.x + root.w / 2 + 30)) * base * view.k;
            view.y = (drawH / 2 - (root.y + root.h / 2 + 30)) * base * view.k;
        }
    }
    applyView();
}

function zoom(by, cx, cy) {
    viewAuto = null;
    const next = Math.min(4, Math.max(0.2, view.k * by));
    const host = $('mmCanvas');
    if (host && cx !== undefined) {
        // Zoom about the pointer, not the centre: zooming about the middle
        // walks the thing you are looking at off the screen.
        const box = host.getBoundingClientRect();
        const px = cx - box.left - box.width / 2;
        const py = cy - box.top - box.height / 2;
        view.x = px - (px - view.x) * (next / view.k);
        view.y = py - (py - view.y) * (next / view.k);
    }
    view.k = next;
    applyView();
}

/** Centre the view on a node — used by search, by AI results and by presenting. */
function centreOn(ids, padding) {
    if (!laid || !ids || !ids.length) return;
    viewAuto = null;
    const host = $('mmCanvas');
    if (!host) return;
    const set = new Set(ids);
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const n of laid.nodes) {
        if (!set.has(n.id)) continue;
        minX = Math.min(minX, n.x); minY = Math.min(minY, n.y);
        maxX = Math.max(maxX, n.x + n.w); maxY = Math.max(maxY, n.y + n.h);
    }
    if (minX === Infinity) return;

    const drawW = laid.width + 60, drawH = laid.height + 60;
    const box = host.getBoundingClientRect();
    // The svg is fitted with preserveAspectRatio="meet", so one drawing unit is
    // this many CSS pixels before our own zoom is applied.
    const base = baseScale();
    if (!base) return;
    const pad = padding === undefined ? 40 : padding;
    const wantW = (maxX - minX) + pad * 2, wantH = (maxY - minY) + pad * 2;
    const k = Math.min(4, Math.max(0.2, Math.min(box.width / (wantW * base), box.height / (wantH * base))));

    const cx = (minX + maxX) / 2 + 30, cy = (minY + maxY) / 2 + 30;
    view.k = k;
    view.x = (drawW / 2 - cx) * base * k;
    view.y = (drawH / 2 - cy) * base * k;
    applyView();
}

/* ── inline editing ────────────────────────────────────────────────────────── */
function select(id) {
    barArmed = true;
    selected = id;
    markSelection();
    positionNodeBar();
    fillDetail();
}

function nodeEl(id) {
    return $('mmCanvas').querySelector(`.mm-node[data-id="${CSS.escape(id)}"]`);
}

function beginEdit(id) {
    const found = Model.find(doc, id);
    if (!found) return;
    const host = $('mmCanvas');
    const g = nodeEl(id);
    if (!g) return;
    editing = id;
    closeDetail();

    const box = g.getBoundingClientRect();
    const hostBox = host.getBoundingClientRect();
    const ta = document.createElement('textarea');
    ta.className = 'mm-edit';
    const org = isOrg();
    const before = org ? editLabel(found.node) : found.node.text;
    ta.value = before;
    ta.setAttribute('aria-label', t('key_rename', 'переименовать'));
    ta.style.left = (box.left - hostBox.left) + 'px';
    ta.style.top = (box.top - hostBox.top) + 'px';
    ta.style.width = Math.max(110, box.width) + 'px';
    ta.style.height = Math.max(34, box.height) + 'px';
    host.appendChild(ta);
    ta.focus();
    ta.select();

    const commit = (keep) => {
        if (editing !== id) return;
        editing = null;
        const next = ta.value.replace(/\s+/g, ' ').trim();
        ta.remove();
        if (keep && next !== before) {
            history.push('edit:' + id);
            if (org) applyEditLabel(found.node, next);
            else found.node.text = next;
            persist();
            ae('node_edit', {});
        }
        draw();
        syncText();
        host.focus();
    };
    ta.addEventListener('blur', () => commit(true));
    ta.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); commit(false); }
        else if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); e.stopPropagation(); commit(true); }
        else if (e.key === 'Tab') { e.preventDefault(); e.stopPropagation(); commit(true); addChild(selected); }
        else e.stopPropagation();
    });
}

/* An org card is edited in place as «Должность — ФИО ×2»: the same grammar as
 * the outline pane, so one habit works in both places. The assistant mark and
 * the unit brackets are the card panel's business, not the label's. */
function editLabel(n) {
    return Org.labelText({ text: n.text, person: n.person, vacant: n.vacant, staff: n.staff }, t('org_vacant_word', 'вакансия'));
}
function applyEditLabel(n, value) {
    const f = Org.parseLabel(value);
    n.text = f.text;
    for (const k of ['person', 'vacant', 'staff']) { if (f[k] !== undefined) n[k] = f[k]; else delete n[k]; }
    if (f.assistant) n.assistant = true;
    if (f.kind) n.kind = 'unit';
}

/* ── operations (each one is an undo step) ─────────────────────────────────── */
function afterChange(opts) {
    persist();
    draw();
    if (!(opts && opts.keepText)) syncText();
}

function addChild(id) {
    if (!id) return;
    history.push(null);
    const n = Model.addChild(doc, id, '');
    if (!n) return;
    selected = n.id;
    afterChange();
    beginEdit(n.id);
    ae('node_add', { kind: 'child' });
}

function addSibling(id) {
    if (!id) return;
    history.push(null);
    const n = Model.addSibling(doc, id, '');
    if (!n) return;
    selected = n.id;
    afterChange();
    beginEdit(n.id);
    ae('node_add', { kind: 'sibling' });
}

function removeNode(id) {
    const f = Model.find(doc, id);
    if (!f || !f.parent) return;
    history.push(null);
    const hidden = Model.countHidden(f.node);
    const next = Model.remove(doc, id);
    if (next) selected = next;
    afterChange();
    ae('node_delete', { hid: hidden });
}

function toggleCollapse(id) {
    if (!Model.toggleCollapse(doc, id)) return;
    history.push(null);
    afterChange({ keepText: true });
    ae('collapse', {});
}

function reorder(id, delta) {
    history.push(null);
    if (Model.reorder(doc, id, delta)) afterChange();
}

function reparent(id, targetId, where) {
    history.push(null);
    if (!Model.move(doc, id, targetId, where)) return false;
    afterChange();
    ae('node_move', { where });
    return true;
}

function setFlag(id, key, value) {
    const f = Model.find(doc, id);
    if (!f) return;
    history.push('flag:' + key + ':' + id);
    if (value === null || value === '' || value === false) delete f.node[key];
    else f.node[key] = value;
    if (key === 'done' && value) f.node.task = true;
    afterChange();
}

/* ── keyboard ──────────────────────────────────────────────────────────────── */
function visibleKids(n) { return n.collapsed ? [] : (n.children || []); }

function navigate(dir) {
    const f = Model.find(doc, selected);
    if (!f) { select(doc.root.id); return; }
    const { node, parent } = f;
    const laidNode = laid && laid.nodes.find((n) => n.id === node.id);
    const side = (laidNode && laidNode.side) || 'right';

    if (side === 'down' || doc.layout === 'down') {
        // The chart runs downward, so "out" is down and siblings are sideways.
        if (dir === 'ArrowDown') { const k = visibleKids(node); if (k.length) select(k[0].id); return; }
        if (dir === 'ArrowUp') { if (parent) select(parent.id); return; }
        return stepAlong(dir === 'ArrowRight' ? 'x+' : 'x-');
    }

    const outward = side === 'left' ? 'ArrowLeft' : 'ArrowRight';
    const inward = side === 'left' ? 'ArrowRight' : 'ArrowLeft';
    if (dir === outward) { const k = visibleKids(node); if (k.length) select(k[0].id); return; }
    if (dir === inward) { if (parent) select(parent.id); return; }
    stepAlong(dir === 'ArrowUp' ? 'y-' : 'y+');
}

/**
 * Move to the nearest node in a direction ON THE DRAWING, not in the tree:
 * what looks like the node above should be the one you reach, even across
 * branches.
 */
function stepAlong(axis) {
    if (!laid) return;
    const cur = laid.nodes.find((n) => n.id === selected);
    if (!cur) return;
    const cx = cur.x + cur.w / 2, cy = cur.y + cur.h / 2;
    let best = null, bestD = Infinity;
    for (const cand of laid.nodes) {
        if (cand.id === cur.id) continue;
        const x = cand.x + cand.w / 2, y = cand.y + cand.h / 2;
        const forward = axis === 'y-' ? cy - y : axis === 'y+' ? y - cy : axis === 'x-' ? cx - x : x - cx;
        if (forward <= 1) continue;
        const lateral = axis[0] === 'y' ? Math.abs(x - cx) : Math.abs(y - cy);
        const d = forward + lateral * 0.55;
        if (d < bestD) { bestD = d; best = cand; }
    }
    if (best) select(best.id);
}

function onKey(e) {
    if (editing) return;
    const tag = (e.target.tagName || '').toLowerCase();
    if (tag === 'input' || tag === 'textarea' || tag === 'select' || e.target.isContentEditable) return;

    const mod = e.ctrlKey || e.metaKey;
    if (mod && e.key.toLowerCase() === 'z' && !e.shiftKey) { e.preventDefault(); history.undo(); return; }
    if (mod && (e.key.toLowerCase() === 'y' || (e.key.toLowerCase() === 'z' && e.shiftKey))) {
        e.preventDefault(); history.redo(); return;
    }
    if (mod && e.key.toLowerCase() === 'f') { e.preventDefault(); const s = $('mmSearch'); if (s) s.focus(); return; }
    if (e.key === 'Escape') {
        if (linkFrom) { linkFrom = null; markSelection(); setLinkHint(false); return; }
        if (query) { clearSearch(); return; }
    }
    if (!selected) return;

    switch (e.key) {
        case 'Tab': e.preventDefault(); addChild(selected); break;
        case 'Enter': e.preventDefault(); addSibling(selected); break;
        case 'F2': e.preventDefault(); beginEdit(selected); break;
        case 'Delete':
        case 'Backspace': e.preventDefault(); removeNode(selected); break;
        case ' ': e.preventDefault(); toggleCollapse(selected); break;
        case 'ArrowUp':
        case 'ArrowDown':
            e.preventDefault();
            if (e.altKey) reorder(selected, e.key === 'ArrowUp' ? -1 : 1);
            else navigate(e.key);
            break;
        case 'ArrowLeft':
        case 'ArrowRight':
            e.preventDefault();
            if (e.altKey) {
                // Outliner grammar: Alt+→ files the node under the sibling
                // above, Alt+← lifts it out to its parent's level.
                if (e.key === 'ArrowRight') { history.push(null); if (Model.demote(doc, selected)) afterChange(); }
                else { history.push(null); if (Model.promote(doc, selected)) afterChange(); }
            } else navigate(e.key);
            break;
        default:
            // Typing over a selected node replaces its text, the way a
            // spreadsheet does — it is the fastest path and costs nothing,
            // since undo holds the old value.
            if (!mod && !e.altKey && e.key.length === 1) {
                e.preventDefault();
                const f = Model.find(doc, selected);
                if (f) { history.push('type:' + selected); f.node.text = e.key; draw(); beginEdit(selected); }
            }
    }
}

/* ── the node bar: actions where the node is ───────────────────────────────── */
function positionNodeBar() {
    const bar = $('mmNodeBar');
    const stageEl = $('mmStage');
    if (!bar || !stageEl) return;
    if (!selected || presenting || !barArmed) { bar.hidden = true; return; }
    const g = nodeEl(selected);
    if (!g) { bar.hidden = true; return; }

    bar.hidden = false;
    const nb = g.getBoundingClientRect();
    const sb = stageEl.getBoundingClientRect();
    const bw = bar.offsetWidth || 260, bh = bar.offsetHeight || 40;
    let left = nb.left - sb.left + nb.width / 2 - bw / 2;
    let top = nb.top - sb.top - bh - 10;
    if (top < 4) top = nb.bottom - sb.top + 10;            // no room above: go below
    left = Math.max(6, Math.min(left, sb.width - bw - 6));
    top = Math.max(4, Math.min(top, sb.height - bh - 4));
    bar.style.left = left + 'px';
    bar.style.top = top + 'px';

    const f = Model.find(doc, selected);
    const isRoot = f && !f.parent;
    bar.querySelectorAll('[data-need-parent]').forEach((b) => { b.disabled = !!isRoot; });
    const doneBtn = bar.querySelector('[data-act="done"]');
    if (doneBtn && f) doneBtn.setAttribute('aria-pressed', f.node.done ? 'true' : 'false');
    if (doneBtn) doneBtn.hidden = isOrg();
    bar.querySelectorAll('[data-org-title]').forEach((b) => {
        const v = b.getAttribute(isOrg() ? 'data-org-title' : 'data-map-title');
        if (v) { b.title = v; b.setAttribute('aria-label', v); }
    });
}

/* ── the detail panel: note, link, icon, colour ────────────────────────────── */
function fillDetail() {
    const panel = $('mmDetail');
    if (!panel || panel.hidden) return;
    const f = Model.find(doc, selected);
    if (!f) { closeDetail(); return; }
    const note = $('mmNote'), url = $('mmUrl');
    if (note) note.value = f.node.note || '';
    if (url) url.value = f.node.url || '';
    panel.querySelectorAll('.mm-swatch').forEach((b) => {
        b.setAttribute('aria-pressed', (f.node.colour || '') === b.getAttribute('data-colour') ? 'true' : 'false');
    });
    panel.querySelectorAll('.mm-icon-pick').forEach((b) => {
        b.setAttribute('aria-pressed', (f.node.icon || '') === b.getAttribute('data-icon') ? 'true' : 'false');
    });
    panel.setAttribute('data-org', isOrg() ? '1' : '0');
    if (isOrg()) {
        const n = f.node;
        const val = (id, v) => { const el = $(id); if (el && document.activeElement !== el) el.value = v; };
        const chk = (id, v) => { const el = $(id); if (el) el.checked = !!v; };
        val('mmOrgPerson', n.person || '');
        val('mmOrgDept', n.dept || '');
        val('mmOrgStaffIn', String(Model.staffOf(n.staff) || 1).replace('.', ','));
        chk('mmOrgVacant', n.vacant);
        chk('mmOrgAssistant', n.assistant);
        chk('mmOrgUnit', n.kind === 'unit');
        const ph = $('mmOrgPhotoDel');
        if (ph) ph.hidden = !n.photo;
        const as = $('mmOrgAssistant');
        if (as) as.disabled = !f.parent;
    }
}

function openDetail() {
    const panel = $('mmDetail');
    if (!panel || !selected) return;
    panel.hidden = false;
    const bar = $('mmNodeBar');
    const sb = $('mmStage').getBoundingClientRect();
    const bb = bar && !bar.hidden ? bar.getBoundingClientRect() : null;
    const pw = panel.offsetWidth || 300;
    let left = bb ? (bb.left - sb.left + bb.width / 2 - pw / 2) : 12;
    let top = bb ? (bb.bottom - sb.top + 8) : 12;
    if (top + (panel.offsetHeight || 220) > sb.height - 6) top = Math.max(6, sb.height - (panel.offsetHeight || 220) - 6);
    panel.style.left = Math.max(6, Math.min(left, sb.width - pw - 6)) + 'px';
    panel.style.top = top + 'px';
    fillDetail();
    // an org card is opened to say WHO holds the position; a map node — to write a note
    const first = isOrg() ? $('mmOrgPerson') : $('mmNote');
    if (first) first.focus();
}

function closeDetail() {
    const panel = $('mmDetail');
    if (panel) panel.hidden = true;
}

/* ── search ────────────────────────────────────────────────────────────────── */
function runSearch(value) {
    query = String(value || '').trim();
    if (!query) { clearSearch(); return; }
    hitList = Model.search(doc, query);
    hits = new Set(hitList);
    hitAt = -1;
    const out = $('mmFound');
    if (out) out.textContent = hitList.length ? String(hitList.length) : t('search_none', 'нет');
    draw();
    ae('search', { hits: hitList.length });
}

function clearSearch() {
    query = '';
    hits = null;
    hitList = [];
    hitAt = -1;
    const s = $('mmSearch');
    if (s) s.value = '';
    const out = $('mmFound');
    if (out) out.textContent = '';
    draw();
}

function jumpToHit(delta) {
    if (!hitList.length) return;
    hitAt = (hitAt + (delta || 1) + hitList.length) % hitList.length;
    const id = hitList[hitAt];
    Model.revealPath(doc, id);
    selected = id;
    draw();
    centreOn([id], 120);
}

/* ── the outline pane: the same document as text ───────────────────────────── */

/**
 * Keep ids across a retype. Cross-links point at ids, and the undo stack reads
 * better when the node you were on is still the node you are on, so a tree
 * rebuilt from text is matched against the old one: by label first (a moved or
 * re-indented line is still that line), then by position.
 */
/* What a retyped line cannot say itself is carried over from the node it was.
 * In a mind map the label is the whole text, so the org card's fields (and the
 * colour, which the outline never wrote) ride along; in an org chart the label
 * already holds person, vacancy and staff units, and only the photo, colour and
 * the department caption need carrying. */
const CARRY_MAP = ['colour', 'photo', 'dept', 'person', 'vacant', 'staff', 'assistant', 'kind'];

function reconcile(oldNode, newNode) {
    if (!oldNode) return newNode;
    newNode.id = oldNode.id;
    for (const key of ['collapsed']) if (oldNode[key] && newNode[key] === undefined) newNode[key] = oldNode[key];
    for (const key of (isOrg() ? Org.CARRIED : CARRY_MAP)) {
        if (oldNode[key] !== undefined && oldNode[key] !== null && oldNode[key] !== false && newNode[key] === undefined) newNode[key] = oldNode[key];
    }
    const pool = (oldNode.children || []).slice();
    const kids = newNode.children || [];
    for (const child of kids) {
        let at = pool.findIndex((o) => o && o.text === child.text);
        if (at < 0) at = pool.findIndex((o) => o && o.text && child.text && o.text.toLowerCase() === child.text.toLowerCase());
        const match = at >= 0 ? pool.splice(at, 1)[0] : null;
        reconcile(match, child);
    }
    return newNode;
}

let textTimer = null;
function onTextInput() {
    textDirty = true;
    clearTimeout(textTimer);
    textTimer = setTimeout(() => {
        const ta = $('mmText');
        if (!ta) return;
        const parsed = Outline.parse(ta.value, { rootText: t('starter_root', 'Моя карта') });
        if (!parsed) return;                       // an empty box is not a command to erase the map
        if (isOrg()) Org.decorateTree(parsed);
        history.push('text');
        const next = Model.adopt({
            root: reconcile(doc.root, parsed),
            links: doc.links,
            layout: doc.layout, theme: doc.theme, shape: doc.shape, id: doc.id, org: doc.org,
            created: doc.created,
        });
        doc = next;
        if (!Model.find(doc, selected)) selected = doc.root.id;
        persist();
        draw();
        ae('text_edit', {});
    }, 320);
}

function syncText() {
    const ta = $('mmText');
    if (!ta) return;
    if (document.activeElement === ta) {
        // Never rewrite what is being typed — but REMEMBER that the map has
        // moved on, or the pane keeps showing a version of the document that
        // no longer exists until some later edit happens to catch it.
        textStale = true;
        return;
    }
    ta.value = Outline.stringify(doc.root, textOpts());
    textDirty = false;
    textStale = false;
}

function textOpts() {
    if (!isOrg()) return {};
    const vw = t('org_vacant_word', 'вакансия');
    return { textOf: (n) => Org.labelText(n, vw) };
}

/* ── view switching (map / both / text) ────────────────────────────────────── */
function setView(mode) {
    const body = $('mmBody');
    if (!body) return;
    body.setAttribute('data-view', mode);
    document.querySelectorAll('#mmView .tc-seg-b').forEach((b) => {
        b.classList.toggle('on', b.getAttribute('data-view') === mode);
        b.setAttribute('aria-pressed', b.getAttribute('data-view') === mode ? 'true' : 'false');
    });
    try { window.__toolSettings && window.__toolSettings.save(SLUG, 'view', mode); } catch (e) {}
    // The stage changed width, so the fitted drawing has to be re-measured.
    requestAnimationFrame(() => { draw(); if (mode !== 'text') openView(); });
}

/* ── library ───────────────────────────────────────────────────────────────── */
function refreshLibrary() {
    const list = $('mmLibList');
    if (!list) return;
    const rows = Store.list();
    list.textContent = '';
    if (!rows.length) {
        const p = document.createElement('p');
        p.className = 'tc-hint';
        p.textContent = t('lib_empty', 'Пока одна карта');
        list.appendChild(p);
        return;
    }
    for (const row of rows) {
        const item = document.createElement('div');
        item.className = 'mm-lib-row' + (row.id === doc.id ? ' on' : '');
        const open = document.createElement('button');
        open.type = 'button';
        open.className = 'mm-lib-open';
        open.setAttribute('data-open', row.id);
        open.innerHTML = '<b></b><span></span>';
        open.querySelector('b').textContent = row.title || t('untitled', 'Без названия');
        open.querySelector('span').textContent = row.nodes + ' · ' + when(row.updated);
        const dup = document.createElement('button');
        dup.type = 'button'; dup.className = 'mm-lib-act'; dup.setAttribute('data-dup', row.id);
        dup.title = t('lib_duplicate', 'Дубликат'); dup.textContent = '⧉';
        const del = document.createElement('button');
        del.type = 'button'; del.className = 'mm-lib-act'; del.setAttribute('data-del', row.id);
        del.title = t('lib_delete', 'Удалить'); del.textContent = '🗑️';
        item.appendChild(open); item.appendChild(dup); item.appendChild(del);
        list.appendChild(item);
    }
}

function when(ts) {
    if (!ts) return '';
    const d = new Date(ts);
    const mins = Math.round((Date.now() - ts) / 60000);
    if (mins < 1) return t('just_now', 'только что');
    if (mins < 60) return mins + ' ' + t('mins_ago', 'мин');
    try {
        return d.toLocaleDateString(window.__LOCALE || undefined, { day: 'numeric', month: 'short' });
    } catch (e) { return d.toISOString().slice(0, 10); }
}

function openMap(id) {
    const next = Store.load(id);
    if (!next) return;
    doc = next;
    selected = doc.root.id;
    history.reset && history.reset();
    draw();
    openView();
    syncText();
    syncOrgUi();
    refreshLibrary();
    Store.setCurrent(doc.id);
    closePops();
    ae('map_open', { org: isOrg() ? 1 : 0 });
}

function newMap(seed) {
    doc = seed || Model.mkDoc(starterStrings());
    doc.id = Model.nid('m');
    doc.created = Date.now();
    selected = doc.root.id;
    Store.save(doc);
    draw();
    openView();
    syncText();
    syncOrgUi();
    refreshLibrary();
    closePops();
}

/* ── file in ───────────────────────────────────────────────────────────────── */
async function openFile(file) {
    if (!file) return;
    let text = '';
    try { text = await file.text(); } catch (e) { toast(t('err_read', 'Не удалось прочитать файл'), 'error'); return; }
    const F = await import('./formats.js');
    const kind = F.sniff(text, file.name);
    let root = null;
    let payload = null;
    try {
        if (kind === 'opml') root = F.fromOpml(text);
        else if (kind === 'freemind') root = F.fromFreemind(text);
        else if (kind === 'json') payload = JSON.parse(text);
        else root = Outline.parse(text, { rootText: file.name.replace(/\.[^.]+$/, '') });
    } catch (e) { root = null; }

    if (payload) {
        newMap(Model.adopt(payload));
    } else if (root) {
        newMap(Model.adopt({ root }));
    } else {
        toast(t('err_import', 'Не удалось разобрать файл'), 'error');
        return;
    }
    toast((t('import_done', 'Готово: пунктов :n') || '').replace(':n', String(Model.countNodes(doc))), 'success');
    ae('import_file', { kind, nodes: Model.countNodes(doc) });
}

/* ── share ─────────────────────────────────────────────────────────────────── */
async function shareLink() {
    try {
        const payload = await Share.encode(doc);
        const url = Share.linkFor(payload, location.href.split('#')[0]);
        if (url.length > Share.SAFE_LINK_CHARS) {
            toast(t('share_too_big', 'Карта слишком большая для ссылки — выгрузите JSON'), 'warning');
            ae('share', { ok: false, len: url.length });
            return;
        }
        let copied = false;
        try { await navigator.clipboard.writeText(url); copied = true; } catch (e) {}
        if (!copied && navigator.share) {
            try { await navigator.share({ url, title: doc.root.text || 'Mind map' }); copied = true; } catch (e) {}
        }
        const out = $('mmShareOut');
        if (out) { out.value = url; out.hidden = false; out.select && out.select(); }
        let photos = false;
        Model.walk(doc.root, (n) => { if (n.photo) photos = true; });
        toast((copied ? t('share_copied', 'Ссылка скопирована') : t('share_ready', 'Ссылка готова'))
            + (photos ? ' ' + t('share_no_photos', 'Фотографии в ссылку не входят.') : ''), 'success');
        ae('share', { ok: true, len: url.length });
    } catch (e) {
        toast(t('err_share', 'Не удалось собрать ссылку'), 'error');
    }
}

async function readShared() {
    const payload = Share.payloadFromHash();
    if (!payload) return false;
    try {
        const incoming = await Share.decode(payload);
        newMap(incoming);
        history.reset && history.reset();
        toast(t('share_opened', 'Открыта карта из ссылки'), 'success');
        ae('share_open', { nodes: Model.countNodes(doc) });
        // The address goes back to the plain page: a reload should not re-open
        // the shared copy over whatever the person has since written.
        if (window.history && window.history.replaceState) {
            window.history.replaceState(null, '', location.pathname + location.search);
        }
        return true;
    } catch (e) {
        toast(t('err_share_open', 'Ссылка повреждена — карта не открылась'), 'error');
        return false;
    }
}

/* ── exports ───────────────────────────────────────────────────────────────── */
function pngLongSide() {
    const sel = $('mmPngSize');
    const v = sel ? parseInt(sel.value, 10) : 2560;
    return isFinite(v) && v > 0 ? v : 2560;
}

async function doExport(kind) {
    const l = layoutNow();
    try {
        if (kind === 'png') {
            const blob = await Exports.renderPng(doc, l, pngLongSide());
            window.__downloadFile(blob, Exports.outName(doc, 'png'));
        } else if (kind === 'copy') {
            await Exports.copyPng(doc, l, Math.min(pngLongSide(), 2560));
            toast(t('copied_image', 'Картинка скопирована'), 'success');
        } else if (kind === 'svg') {
            const blob = new Blob([Exports.svgText(doc, l)], { type: 'image/svg+xml;charset=utf-8' });
            window.__downloadFile(blob, Exports.outName(doc, 'svg'));
        } else if (kind === 'pdf') {
            await exportPdf(l);
        } else {
            Exports.downloadText(doc, kind);
        }
        ae('export', { fmt: kind, nodes: Model.countNodes(doc) });
        try {
            window.__aeToolComplete && window.__aeToolComplete({
                fmt: kind, nodes: Model.countNodes(doc), layout: doc.layout,
            });
        } catch (e) {}
    } catch (err) {
        if (kind === 'copy' && String(err && err.message) === 'no-clipboard') {
            toast(t('err_clipboard', 'Браузер не разрешает копировать картинку — скачайте PNG'), 'warning');
            return;
        }
        window.__handleError ? window.__handleError(err, 'mind-map/' + kind, { say: t('err_export', 'Не удалось собрать файл') })
                            : toast(t('err_export', 'Не удалось собрать файл'), 'error');
    }
}

async function exportPdf(l) {
    const btn = $('mmPdf');
    if (btn) btn.disabled = true;
    try {
        const mod = await import('./pdf.js');
        const blob = await mod.build({
            laid: l,
            title: doc.root.text,
            paper: ($('mmPaper') && $('mmPaper').value) || 'a4',
            mode: ($('mmPdfMode') && $('mmPdfMode').value) || 'auto',
            theme: doc.theme,
            shape: doc.shape,
            crossLinks: doc.links || [],
            T,
        });
        window.__downloadFile(blob, Exports.outName(doc, 'pdf'));
    } catch (err) {
        window.__handleError ? window.__handleError(err, 'mind-map/pdf', { say: t('err_pdf') }) : toast(t('err_pdf'), 'error');
        try { window.__aeCritical && window.__aeCritical('pdf_failed', { m: String(err && err.message) }); } catch (e) {}
    } finally {
        if (btn) btn.disabled = false;
    }
}

/* ── the assistant ─────────────────────────────────────────────────────────── */
async function askAi(mode) {
    const btn = $('mmAiGo');
    const input = $('mmAiTopic');
    const status = $('mmAiStatus');
    if (!CFG.aiUrl) return;
    const f = Model.find(doc, selected) || { node: doc.root };
    const topic = mode === 'expand'
        ? (f.node.text || doc.root.text)
        : ((input && input.value.trim()) || doc.root.text);
    if (!topic) { toast(t('ai_need_topic', 'Напишите тему'), 'warning'); return; }

    if (btn) btn.disabled = true;
    if (status) { status.hidden = false; status.className = 'tc-msg'; status.textContent = t('ai_working', 'Думаю…'); }
    if (window.__lanePay) window.__lanePay.hide($('mmAiPay'));
    try {
        const mod = await import('./ai.js');
        const path = Model.pathTo(doc, f.node.id).map((id) => {
            const p = Model.find(doc, id);
            return p ? p.node.text : '';
        }).filter(Boolean);
        const res = await mod.ask({
            url: CFG.aiUrl,
            mode,
            topic,
            path: path.concat(mode === 'expand' ? [f.node.text] : []),
            siblings: (f.node.children || []).map((k) => k.text),
            lang: CFG.locale,
            pay: !!(window.__lanePay && window.__lanePay.agreed('mind-map')),
        });
        const parsed = Outline.parse(res.outline, { rootText: topic });
        if (!parsed) throw new Error('empty');

        history.push(null);
        if (mode === 'expand') {
            const target = Model.find(doc, f.node.id);
            const incoming = (parsed.text === topic ? (parsed.children || []) : [parsed]);
            for (const kid of incoming) {
                target.node.children.push(Model.adopt({ root: kid }).root);
            }
            target.node.collapsed = false;
        } else if (Model.countNodes(doc) > 4) {
            // A map someone has already built is not scratch paper. Undo would
            // bring it back, but "the assistant ate my work" is a thing people
            // never forgive, and a new map costs nothing — the shelf holds both.
            newMap(Model.adopt({ root: parsed }));
            return;
        } else {
            doc.root = Model.adopt({ root: parsed }).root;
            selected = doc.root.id;
        }
        afterChange();
        centreOn([selected], 90);
        if (status) { status.className = 'tc-msg good'; status.textContent = t('ai_done', 'Готово — правьте как обычные пункты'); }
        const payBox = $('mmAiPay');
        if (window.__lanePay && payBox) {
            if (!window.__lanePay.paid(payBox, res)) window.__lanePay.hide(payBox);
        }
        // The menu may already be closed under the new map: the charge is said
        // where it is seen.
        if (res.paid_tokens > 0 && res.paid_text) toast(res.paid_text, 'info');
        ae('ai', { mode, nodes: Model.countNodes(doc) });
    } catch (err) {
        const msg = err && err.serverMessage
            ? err.serverMessage
            : (err && err.status === 0 ? t('ai_offline', 'Нет связи с сервером') : t('ai_failed', 'Не получилось — попробуйте иначе сформулировать'));
        const offer = $('mmAiOffer');
        const payBox = $('mmAiPay');
        // A refusal with a price is an offer, not an error: the pay box says
        // it (with the run button), and the status line steps aside.
        const priced = !!(window.__lanePay && payBox && err && err.data
            && window.__lanePay.show(payBox, err.data, { slug: 'mind-map', run: () => askAi(mode) }));
        if (status) {
            status.hidden = priced;
            if (!priced) { status.className = 'tc-msg ' + (err && err.premium ? 'warn' : 'bad'); status.textContent = msg; }
        }
        if (offer) offer.hidden = priced || !(err && (err.premium || err.quota));
        ae('ai_refused', { status: (err && err.status) || 0 });
    } finally {
        if (btn) btn.disabled = false;
    }
}

/* ── presentation ──────────────────────────────────────────────────────────── */
function startPresent() {
    const host = $('mmStage');
    if (!host || presenting) return;
    closePops();
    closeDetail();
    presenting = Present.start({
        host,
        doc,
        T,
        onStep: (step) => {
            focusIds = new Set(step.ids);
            draw();
            centreOn(step.ids, 60);
        },
        onExit: () => {
            presenting = null;
            focusIds = null;
            draw();
            openView();
        },
    });
    ae('present', { steps: presenting ? presenting.steps.length : 0 });
}

/* ── popovers ──────────────────────────────────────────────────────────────── */
const supportsPopover = typeof HTMLElement !== 'undefined'
    && HTMLElement.prototype.hasOwnProperty('popover');

function closePops() {
    document.querySelectorAll('.mm-pop').forEach((p) => {
        if (supportsPopover && p.matches(':popover-open')) { try { p.hidePopover(); } catch (e) {} }
        p.classList.remove('open');
    });
    document.querySelectorAll('[data-pop]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
}

function togglePop(id, anchor) {
    const pop = $(id);
    if (!pop) return;
    const isOpen = supportsPopover ? pop.matches(':popover-open') : pop.classList.contains('open');
    closePops();
    if (isOpen) return;
    if (supportsPopover) { try { pop.showPopover(); } catch (e) { pop.classList.add('open'); } }
    else pop.classList.add('open');
    if (anchor) {
        // Placed by hand rather than with CSS anchor positioning: that is
        // Chromium-only today, and a menu that lands in the corner for everyone
        // else is worse than one placed with a little arithmetic.
        //
        // Measured AFTER the popover is shown — a hidden element reports the
        // height its content wants, not the height it will have under
        // max-height, and the first cut of this flipped a 540px menu above a
        // button 100px down the page because it believed 900.
        const ab = anchor.getBoundingClientRect();
        const narrow = window.innerWidth <= 620;
        if (!narrow) {
            const box = pop.getBoundingClientRect();
            const gap = 8;
            // the consent banner owns the bottom of the screen and publishes its height
            let inset = 0;
            try { inset = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--cc-bottom-inset')) || 0; } catch (e) {}
            const below = window.innerHeight - inset - ab.bottom - gap * 2;
            const above = ab.top - gap * 2;
            const useBelow = below >= Math.min(box.height, 240) || below >= above;
            const room = Math.max(160, useBelow ? below : above);
            pop.style.maxHeight = Math.min(room, 560) + 'px';
            const h = Math.min(box.height, room);
            pop.style.top = (useBelow ? ab.bottom + gap : Math.max(gap, ab.top - gap - h)) + 'px';
            pop.style.left = Math.max(8, Math.min(ab.left, window.innerWidth - box.width - 8)) + 'px';
            pop.style.width = '';
        } else {
            // The phone gets a bottom sheet (CSS owns the geometry) — a card
            // pinned under a toolbar button covers the very thing it is about.
            pop.style.top = '';
            pop.style.left = '';
            pop.style.width = '';
            pop.style.maxHeight = '';
        }
    }
    anchor && anchor.setAttribute('aria-expanded', 'true');
    if (id === 'mmLib') refreshLibrary();
    // the importer is a separate module: fetch it while the person reads the menu
    if (id === 'mmOrg') { orgImporter().catch(() => {}); }
}

function setLinkHint(on) {
    const hint = $('mmLinkHint');
    if (hint) hint.hidden = !on;
}

/* ── dragging a node onto another parent ───────────────────────────────────── */
function wireDrag(host) {
    let drag = null;
    let pan = null;
    const pointers = new Map();
    let pinch = null;

    const dropTargetAt = (x, y) => {
        const el = document.elementFromPoint(x, y);
        const g = el && el.closest ? el.closest('.mm-node') : null;
        return g ? g.getAttribute('data-id') : null;
    };

    const showDrop = (targetId, where) => {
        host.querySelectorAll('.mm-node.drop, .mm-node.drop-before, .mm-node.drop-after')
            .forEach((g) => g.classList.remove('drop', 'drop-before', 'drop-after'));
        if (!targetId) return;
        const g = nodeEl(targetId);
        if (g) g.classList.add(where === 'child' ? 'drop' : (where === 'before' ? 'drop-before' : 'drop-after'));
    };

    host.addEventListener('pointerdown', (e) => {
        if (presenting) return;
        pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
        if (pointers.size === 2) {
            const pts = [...pointers.values()];
            pinch = { d: Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y), k: view.k };
            pan = null; drag = null;
            return;
        }
        const g = e.target.closest && e.target.closest('.mm-node');
        if (e.target.closest && e.target.closest('.mm-handle')) return;
        if (g) {
            drag = { id: g.getAttribute('data-id'), x: e.clientX, y: e.clientY, live: false };
        } else {
            pan = { x: e.clientX, y: e.clientY, vx: view.x, vy: view.y };
        }
        try { host.setPointerCapture(e.pointerId); } catch (err) {}
    });

    host.addEventListener('pointermove', (e) => {
        if (pointers.has(e.pointerId)) pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
        if (pinch && pointers.size === 2) {
            const pts = [...pointers.values()];
            const d = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
            if (pinch.d > 0) { viewAuto = null; view.k = Math.min(4, Math.max(0.2, pinch.k * (d / pinch.d))); applyView(); }
            return;
        }
        if (pan) {
            viewAuto = null;
            view.x = pan.vx + (e.clientX - pan.x);
            view.y = pan.vy + (e.clientY - pan.y);
            applyView();
            return;
        }
        if (!drag) return;
        if (!drag.live && Math.hypot(e.clientX - drag.x, e.clientY - drag.y) < 7) return;
        const f = Model.find(doc, drag.id);
        if (!f || !f.parent) { drag = null; return; }     // the centre does not move
        drag.live = true;
        host.classList.add('dragging');
        const targetId = dropTargetAt(e.clientX, e.clientY);
        drag.target = null;
        if (targetId && targetId !== drag.id && !Model.isAncestor(doc, drag.id, targetId)) {
            const g = nodeEl(targetId);
            const box = g.getBoundingClientRect();
            const rel = (e.clientY - box.top) / Math.max(1, box.height);
            const tf = Model.find(doc, targetId);
            let where = 'child';
            if (tf && tf.parent) where = rel < 0.28 ? 'before' : (rel > 0.72 ? 'after' : 'child');
            drag.target = { id: targetId, where };
            showDrop(targetId, where);
        } else showDrop(null);
    });

    const endPointer = (e) => {
        pointers.delete(e.pointerId);
        if (pointers.size < 2) pinch = null;
        pan = null;
        if (!drag) return;
        const d = drag; drag = null;
        host.classList.remove('dragging');
        showDrop(null);
        if (d.live && d.target) {
            if (reparent(d.id, d.target.id, d.target.where)) {
                selected = d.id;
                markSelection();
                positionNodeBar();
            }
        } else if (!d.live) {
            select(d.id);
            host.focus();
        }
    };
    host.addEventListener('pointerup', endPointer);
    host.addEventListener('pointercancel', endPointer);
}

/* ── organisation chart ────────────────────────────────────────────────────── */

const bucket = (n) => (n <= 0 ? '0' : n <= 5 ? '1-5' : n <= 20 ? '6-20' : n <= 60 ? '21-60' : n <= 200 ? '61-200' : '200+');

function orgSettings() {
    if (!doc.org) doc.org = Model.plainOrg({});
    return doc.org;
}

/** Everything on the page that depends on «is this an org chart». */
function syncOrgUi() {
    const org = isOrg();
    document.querySelectorAll('[data-org-only]').forEach((el) => { el.hidden = !org; });
    document.querySelectorAll('[data-map-only]').forEach((el) => { el.hidden = org; });
    document.querySelectorAll('[data-layout]').forEach((x) => x.classList.toggle('on', x.getAttribute('data-layout') === doc.layout));
    document.querySelectorAll('[data-theme-key]').forEach((x) => x.classList.toggle('on', x.getAttribute('data-theme-key') === doc.theme));
    document.querySelectorAll('[data-shape]').forEach((x) => x.classList.toggle('on', x.getAttribute('data-shape') === doc.shape));
    const ta = $('mmText');
    if (ta) ta.placeholder = org ? t('org_text_ph', ta.placeholder) : (ta.getAttribute('data-ph-map') || ta.placeholder);
    const hint = $('mmTextHint');
    if (hint) hint.textContent = org ? t('org_text_hint', '') : (hint.getAttribute('data-map') || hint.textContent);
    if (!org) return;
    const o = orgSettings();
    const val = (id, v) => { const el = $(id); if (el && document.activeElement !== el) el.value = v; };
    const chk = (id, v) => { const el = $(id); if (el) el.checked = !!v; };
    val('mmOrgName', o.name || '');
    chk('mmOrgPhotos', o.photos);
    chk('mmOrgCount', o.count);
    chk('mmOrgCompact', o.compact);
    chk('mmOrgApOn', o.approve.on);
    val('mmOrgApPos', o.approve.pos || '');
    val('mmOrgApFio', o.approve.fio || '');
    val('mmOrgApDate', o.approve.date || '');
    const dt = $('mmOrgDepts');
    if (dt && dt.open) renderDeptTable();
}

function setLayout(v) {
    if (!v || v === doc.layout) { syncOrgUi(); return; }
    history.push(null);
    doc.layout = v;
    if (v === 'org') { orgSettings(); stage('org'); }
    persist();
    draw();
    openView();
    syncText();
    syncOrgUi();
    fillDetail();
    ae('layout', { v });
}

function setOrgOption(key, value) {
    const o = orgSettings();
    history.push('org:' + key);
    if (key.indexOf('approve.') === 0) o.approve[key.slice(8)] = value;
    else o[key] = value;
    persist();
    if (key === 'photos' || key === 'count' || key === 'compact') { draw(); if (viewAuto) openView(); }
    ae('org_option', { k: key.replace('approve.', 'ap_'), v: typeof value === 'boolean' ? (value ? 1 : 0) : (value ? 1 : 0) });
}

/** Column names of the Excel template and of the exported list — in the page's language. */
function orgLabels() {
    return {
        fio: t('col_fio', 'ФИО'), pos: t('col_pos', 'Должность'), dept: t('col_dept', 'Подразделение'),
        boss: t('col_boss', 'Руководитель'), staff: t('col_staff', 'Количество штатных единиц'),
        code: t('col_code', 'Код подразделения'), tab: t('col_tab', 'Табельный номер'),
        boss_example: t('col_boss_ex', 'Иванов Иван Иванович'),
    };
}

function orgMessage(text, bad) {
    const box = $('mmOrgImpMsg');
    if (!box) { toast(text, bad ? 'error' : 'success'); return; }
    box.hidden = !text;
    box.className = 'tc-msg' + (bad ? ' bad' : ' good');
    box.textContent = text || '';
}

function orgTemplate(key) {
    const src = t('org_tpl_' + key, '');
    const parsed = src ? Outline.parse(src, { rootText: t('org_root', 'Организация') }) : null;
    if (!parsed) return false;
    Org.decorateTree(parsed);
    newMap(Model.adopt({ root: parsed, layout: 'org', theme: doc ? doc.theme : 'aurora', org: { name: '' } }));
    stage('org');
    ae('org_template', { tpl: key, n: bucket(Model.countNodes(doc)) });
    return true;
}

async function orgImporter() {
    return import('./org-import.js');
}

function orgApply(res, via, I) {
    const pal = Render.theme(doc.theme).branches.map((b) => b.s);
    const b = I.buildTree(res, { rootName: t('org_root', 'Организация'), palette: pal });
    const keys = Object.keys((res && res.mapping) || {}).filter((k) => k.charAt(0) !== '_').sort().join(',').slice(0, 60);
    if (!b.root) {
        orgMessage(t('org_imp_none', 'В таблице не нашлось ни ФИО, ни должностей'), true);
        try { window.__aeDiagnostic && window.__aeDiagnostic({ issue: 'org_import_empty', via, map: keys }); } catch (e) {}
        return false;
    }
    newMap(Model.adopt({ root: b.root, layout: 'org', theme: doc.theme, org: { name: b.orgName || '' } }));
    let msg = t('org_imp_done_' + b.mode, t('org_imp_done', 'Готово: карточек :n')).replace(':n', String(b.count));
    const un = (b.warnings || []).find((w) => w.indexOf('boss_unresolved:') === 0);
    if (un) msg += ' ' + t('org_imp_unresolved', 'Не нашёлся руководитель у :n — они встали наверх.').replace(':n', un.split(':')[1]);
    orgMessage(msg, false);
    stage('org');
    ae('org_import', { via, mode: b.mode, n: bucket(b.count), map: keys });
    return true;
}

async function orgImportFile(file) {
    if (!file) return;
    orgMessage(t('org_imp_reading', 'Читаю таблицу…'), false);
    try {
        const I = await orgImporter();
        const { readRosterFile } = await import('../staff-roster/read.js');
        const res = await readRosterFile(file, I.orgFields(orgLabels()), {
            sheetjs: ASSETS.sheetjs, import: { need: ['fio', 'pos'] }, keepRows: true,
        });
        orgApply(res, 'file', I);
    } catch (err) {
        const code = err && err.code;
        orgMessage(code === 'bad_type' ? t('org_imp_bad_type', 'Нужна таблица: Excel (.xlsx, .xls), ODS или CSV') : t('org_imp_failed', 'Не удалось прочитать таблицу'), true);
        try { window.__aeDiagnostic && window.__aeDiagnostic({ issue: 'org_import_failed', code: code || 'error' }); } catch (e) {}
        if (!code && window.__handleError) window.__handleError(err, 'mind_map_org_import', { say: t('org_imp_failed', 'Не удалось прочитать таблицу') });
    }
}

async function orgImportText(text) {
    if (!String(text || '').trim()) { orgMessage(t('org_paste_empty', 'Вставьте строки из таблицы'), true); return false; }
    orgMessage(t('org_imp_reading', 'Читаю таблицу…'), false);
    const I = await orgImporter();
    return orgApply(I.importPasted(text, I.orgFields(orgLabels())), 'paste', I);
}

async function loadScriptOnce(url, expect, label) {
    if (expect && window[expect]) return window[expect];
    if (!url) throw new Error(label + '_no_url');
    await window.__vendorScript(url, { expect, label });
    return window[expect];
}

async function orgTemplateXlsx(btn) {
    try {
        if (btn) btn.disabled = true;
        const I = await orgImporter();
        const R = await import('../staff-roster/roster.js');
        const ff = await loadScriptOnce(ASSETS.fflate, 'fflate', 'fflate');
        const XW = await loadScriptOnce(ASSETS.xlsxWriter, 'XlsxWriter', 'xlsx-writer');
        const keep = ['fio', 'pos', 'dept', 'boss', 'staff'];
        // no example row: in an org chart a sample person would become a stray card on import
        const fields = I.orgFields(orgLabels()).filter((f) => keep.indexOf(f.key) >= 0).map((f) => Object.assign({}, f, { example: '' }));
        const sheet = R.templateSheet(fields, { name: t('xlsx_sheet_people', 'Сотрудники') });
        const bytes = XW.build({ title: t('org_file', 'Оргструктура'), sheets: [sheet] }, ff.zipSync);
        await window.__downloadFile(new Blob([bytes], { type: XW.MIME }), Exports.outName({ root: { text: t('org_template_file', 'Шаблон оргструктуры') } }, 'xlsx'));
        ae('org_template_xlsx', {});
    } catch (err) {
        window.__handleError && window.__handleError(err, 'mind_map_org_template', { say: t('err_export', 'Не удалось собрать файл') });
    } finally { if (btn) btn.disabled = false; }
}

function renderDeptTable() {
    const box = $('mmOrgDeptTable');
    if (!box || !isOrg()) return;
    const rows = Org.deptTable(doc.root);
    const named = Org.namesPeople(doc.root);
    box.textContent = '';
    if (!rows.length) {
        const p = document.createElement('p');
        p.className = 'tc-hint';
        p.textContent = t('org_depts_none', 'Подразделений пока нет');
        box.appendChild(p);
        return;
    }
    const table = document.createElement('table');
    table.className = 'mm-depts';
    const head = document.createElement('tr');
    [t('org_depts_name', 'Подразделение'), named ? t('org_depts_people', 'Людей') : '', t('org_depts_staff', 'Ставок'), t('org_depts_vacant', 'Вакансий')]
        .forEach((h, i) => { if (!h && i === 1) return; const th = document.createElement('th'); th.textContent = h; head.appendChild(th); });
    table.appendChild(head);
    let tot = { staff: 0, vacant: 0, people: 0 };
    for (const r of rows) {
        const tr = document.createElement('tr');
        const cells = [r.name, named ? OrgRender.fmtNum(r.people) : null, OrgRender.fmtNum(r.staff), r.vacant ? OrgRender.fmtNum(r.vacant) : '—'];
        cells.forEach((c, i) => {
            if (c === null) return;
            const td = document.createElement('td');
            td.textContent = c;
            if (i === 0) td.style.paddingInlineStart = (6 + Math.max(0, (r.depth || 1) - 1) * 12) + 'px';
            tr.appendChild(td);
        });
        table.appendChild(tr);
    }
    const all = Org.headcount(doc.root);
    tot = all;
    const tr = document.createElement('tr');
    tr.className = 'tot';
    [t('org_depts_total', 'Всего'), named ? OrgRender.fmtNum(tot.people) : null, OrgRender.fmtNum(tot.staff), tot.vacant ? OrgRender.fmtNum(tot.vacant) : '—']
        .forEach((c) => { if (c === null) return; const td = document.createElement('td'); td.textContent = c; tr.appendChild(td); });
    table.appendChild(tr);
    box.appendChild(table);
}

/** A photo becomes a 160 px square JPEG, cropped to the centre: small enough to
 *  keep dozens of cards in the browser's storage, sharp enough for an A3 print. */
function photoToDataUrl(file) {
    return new Promise((resolve, reject) => {
        if (!file || !/^image\//.test(file.type || '') && !/\.(jpe?g|png|webp|gif|bmp|heic)$/i.test(file.name || '')) {
            reject(Object.assign(new Error('not_image'), { code: 'not_image' })); return;
        }
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
            try {
                const S = 160;
                const side = Math.min(img.naturalWidth, img.naturalHeight);
                if (!side) throw new Error('empty_image');
                const c = document.createElement('canvas');
                c.width = S; c.height = S;
                const g = c.getContext('2d');
                g.fillStyle = '#ffffff';
                g.fillRect(0, 0, S, S);
                g.drawImage(img, (img.naturalWidth - side) / 2, (img.naturalHeight - side) / 2, side, side, 0, 0, S, S);
                URL.revokeObjectURL(url);
                resolve(c.toDataURL('image/jpeg', 0.86));
            } catch (e) { URL.revokeObjectURL(url); reject(e); }
        };
        img.onerror = () => { URL.revokeObjectURL(url); reject(Object.assign(new Error('image_decode'), { code: 'not_image' })); };
        img.src = url;
    });
}

async function setPhoto(file) {
    if (!selected || !file) return;
    try {
        const data = await photoToDataUrl(file);
        setFlag(selected, 'photo', data);
        fillDetail();
        ae('org_photo', {});
    } catch (err) {
        toast(t('org_photo_bad', 'Это не картинка — нужен JPG, PNG или WebP'), 'warning');
    }
}

/* ── org chart outputs: the one thing the daily cap counts ──────────────────── */

/** The cap unit is THIS chart (its id, stamped when it was created): every
 *  format of the same chart on the same day is one unit, a new chart — another. */
function orgRun() {
    if (!window.__toolLimit) return { commit() {}, abort() {} };
    return window.__toolLimit.begin(SLUG, [Org.docKey(doc)]);
}

function orgFileBase() {
    const o = orgSettings();
    const name = (o.name || '').trim();
    return t('org_file', 'Оргструктура') + (name ? ' — ' + name : '');
}

function orgOutName(ext) {
    return Exports.outName({ root: { text: orgFileBase() } }, ext);
}

async function doOrgExport(kind, btn) {
    if (!isOrg()) return;
    const run = orgRun();
    if (!run) { ae('org_export_walled', { fmt: kind }); return; }
    if (btn) { btn.disabled = true; btn.classList.add('is-busy'); }
    let ok = false;
    try {
        const l = layoutNow();
        if (kind === 'png') {
            const blob = await Exports.renderPng(doc, l, orgPngSide());
            ok = await window.__downloadFile(blob, orgOutName('png'));
        } else if (kind === 'svg') {
            const blob = new Blob([Exports.svgText(doc, l)], { type: 'image/svg+xml;charset=utf-8' });
            ok = await window.__downloadFile(blob, orgOutName('svg'));
        } else if (kind === 'copy') {
            await Exports.copyPng(doc, l, Math.min(orgPngSide(), 2560));
            ok = true;
            toast(t('copied_image', 'Картинка скопирована'), 'success');
        } else if (kind === 'pdf') {
            const mod = await import('./pdf.js');
            const blob = await mod.build({
                laid: l, title: orgFileBase(), paper: ($('mmOrgPaper') && $('mmOrgPaper').value) || 'a4',
                mode: ($('mmOrgPdfMode') && $('mmOrgPdfMode').value) || 'auto',
                orientation: ($('mmOrgOrient') && $('mmOrgOrient').value) || 'landscape',
                theme: doc.theme, shape: doc.shape, crossLinks: doc.links || [], T,
                org: orgSettings(), orgTable: $('mmOrgPdfTable') && $('mmOrgPdfTable').checked ? Org.deptTable(doc.root) : null,
                orgTotals: Org.headcount(doc.root), named: Org.namesPeople(doc.root),
            });
            ok = await window.__downloadFile(blob, orgOutName('pdf'));
            if (ok) ae('org_pdf_plan', { mode: blob.__plan && blob.__plan.mode, pages: blob.__plan ? blob.__plan.pages.length : 0 });
        } else if (kind === 'pptx') {
            const mod = await import('./org-pptx.js');
            const blob = await mod.build({
                doc, laid: l, T, title: orgFileBase(), theme: Render.theme(doc.theme),
                pptxgen: ASSETS.pptxgen, fflate: ASSETS.fflate, named: Org.namesPeople(doc.root),
            });
            ok = await window.__downloadFile(blob, orgOutName('pptx'));
            if (ok) ae('org_pptx_plan', { slides: blob.__slides || 0, conn: blob.__connectors ? 1 : 0 });
        } else if (kind === 'xlsx') {
            const ff = await loadScriptOnce(ASSETS.fflate, 'fflate', 'fflate');
            const XW = await loadScriptOnce(ASSETS.xlsxWriter, 'XlsxWriter', 'xlsx-writer');
            const mod = await import('./org-xlsx.js');
            const bytes = XW.build(mod.workbook(doc, T, orgLabels()), ff.zipSync);
            ok = await window.__downloadFile(new Blob([bytes], { type: XW.MIME }), orgOutName('xlsx'));
        }
        if (ok) {
            run.commit();
            stage('export');
            ae('org_export', { fmt: kind, n: bucket(Model.countNodes(doc)) });
            try {
                window.__aeToolComplete && window.__aeToolComplete({ fmt: kind, mode: 'org', nodes: bucket(Model.countNodes(doc)) });
            } catch (e) {}
        } else run.abort();
    } catch (err) {
        run.abort();
        if (kind === 'copy' && String(err && err.message) === 'no-clipboard') {
            toast(t('err_clipboard', 'Браузер не разрешает копировать картинку — скачайте PNG'), 'warning');
        } else {
            const say = kind === 'pdf' ? t('err_pdf', 'Не удалось собрать PDF') : t('err_export', 'Не удалось собрать файл');
            window.__handleError ? window.__handleError(err, 'mind_map_org_' + kind, { say }) : toast(say, 'error');
            try { window.__aeCritical && window.__aeCritical('org_export_failed', { fmt: kind, m: String(err && err.message).slice(0, 120) }); } catch (e) {}
        }
    } finally {
        if (btn) { btn.disabled = false; btn.classList.remove('is-busy'); }
    }
}

function orgPngSide() {
    const sel = $('mmOrgPngSize');
    const v = sel ? parseInt(sel.value, 10) : 2560;
    return isFinite(v) && v > 0 ? v : 2560;
}

/* ── wiring ────────────────────────────────────────────────────────────────── */
function main() {
    const host = $('mmCanvas');
    if (!host) return;

    doc = Store.opening() || Model.mkDoc(starterStrings());
    if (!Store.currentId()) Store.save(doc);
    selected = doc.root.id;

    const savedView = (() => {
        try { return window.__toolSettings && window.__toolSettings.load(SLUG, 'view', null); } catch (e) { return null; }
    })();
    setView(savedView || (window.innerWidth >= 900 ? 'split' : 'map'));

    draw();
    openView();
    syncText();
    syncOrgUi();
    refreshLibrary();
    stage('ready');
    readShared();

    /* ── canvas interaction ── */
    host.addEventListener('click', (e) => {
        const handle = e.target.closest('.mm-handle');
        if (handle) { toggleCollapse(handle.getAttribute('data-toggle')); return; }
        const g = e.target.closest('.mm-node');
        if (!g) { if (linkFrom) { linkFrom = null; setLinkHint(false); markSelection(); } return; }
        const id = g.getAttribute('data-id');
        if (linkFrom && id !== linkFrom) {
            history.push(null);
            Model.linkNodes(doc, linkFrom, id, '');
            linkFrom = null;
            setLinkHint(false);
            afterChange({ keepText: true });
            ae('cross_link', {});
            return;
        }
        select(id);
        host.focus();
    });
    host.addEventListener('dblclick', (e) => {
        const g = e.target.closest('.mm-node');
        if (g) beginEdit(g.getAttribute('data-id'));
    });
    host.addEventListener('keydown', onKey);
    document.addEventListener('keydown', (e) => {
        if (document.activeElement === host || host.contains(document.activeElement)) return;
        const tag = (e.target.tagName || '').toLowerCase();
        if (tag === 'input' || tag === 'textarea') return;
        if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') onKey(e);
    });
    host.addEventListener('wheel', (e) => {
        if (!e.ctrlKey) return;              // plain scroll still scrolls the page
        e.preventDefault();
        zoom(e.deltaY < 0 ? 1.12 : 1 / 1.12, e.clientX, e.clientY);
    }, { passive: false });
    wireDrag(host);

    /* ── file drop ── */
    const stageEl = $('mmStage');
    if (stageEl) {
        ['dragenter', 'dragover'].forEach((ev) => stageEl.addEventListener(ev, (e) => {
            if (!e.dataTransfer || !Array.from(e.dataTransfer.types || []).includes('Files')) return;
            e.preventDefault();
            stageEl.classList.add('over');
        }));
        ['dragleave', 'drop'].forEach((ev) => stageEl.addEventListener(ev, (e) => {
            if (ev === 'drop') {
                e.preventDefault();
                const file = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
                if (file) openFile(file);
            }
            stageEl.classList.remove('over');
        }));
    }

    const on = (id, ev, fn) => { const el = $(id); if (el) el.addEventListener(ev, fn); };

    /* ── toolbar ── */
    document.querySelectorAll('[data-pop]').forEach((b) => {
        b.addEventListener('click', (e) => { e.stopPropagation(); togglePop(b.getAttribute('data-pop'), b); });
    });
    document.addEventListener('click', (e) => {
        if (e.target.closest('.mm-pop') || e.target.closest('[data-pop]')) return;
        closePops();
    });

    on('mmUndo', 'click', () => history.undo());
    on('mmRedo', 'click', () => history.redo());
    on('mmZoomIn', 'click', () => zoom(1.15));
    on('mmZoomOut', 'click', () => zoom(1 / 1.15));
    on('mmFit', 'click', fit);
    on('mmPresent', 'click', startPresent);
    on('mmShare', 'click', shareLink);

    on('mmSearch', 'input', (e) => runSearch(e.target.value));
    on('mmSearch', 'keydown', (e) => {
        if (e.key === 'Enter') { e.preventDefault(); jumpToHit(e.shiftKey ? -1 : 1); }
        else if (e.key === 'Escape') { e.preventDefault(); clearSearch(); }
    });

    document.querySelectorAll('#mmView .tc-seg-b').forEach((b) => {
        b.addEventListener('click', () => setView(b.getAttribute('data-view')));
    });

    on('mmText', 'input', onTextInput);
    on('mmText', 'blur', () => {
        if (textDirty) { clearTimeout(textTimer); onTextInput(); }
        else if (textStale) syncText();
    });

    /* ── look ── */
    document.querySelectorAll('[data-layout]').forEach((b) => b.addEventListener('click', () => setLayout(b.getAttribute('data-layout'))));
    document.querySelectorAll('[data-shape]').forEach((b) => b.addEventListener('click', () => {
        doc.shape = b.getAttribute('data-shape');
        document.querySelectorAll('[data-shape]').forEach((x) => x.classList.toggle('on', x === b));
        persist(); draw();
    }));
    document.querySelectorAll('[data-theme-key]').forEach((b) => b.addEventListener('click', () => {
        doc.theme = b.getAttribute('data-theme-key');
        document.querySelectorAll('[data-theme-key]').forEach((x) => x.classList.toggle('on', x === b));
        persist(); draw();
    }));

    /* ── node bar ── */
    const bar = $('mmNodeBar');
    if (bar) bar.addEventListener('click', (e) => {
        const b = e.target.closest('[data-act]');
        if (!b || !selected) return;
        const act = b.getAttribute('data-act');
        if (act === 'child') addChild(selected);
        else if (act === 'sibling') addSibling(selected);
        else if (act === 'delete') removeNode(selected);
        else if (act === 'edit') beginEdit(selected);
        else if (act === 'detail') { const p = $('mmDetail'); p && p.hidden ? openDetail() : closeDetail(); }
        else if (act === 'done') {
            const f = Model.find(doc, selected);
            if (f) setFlag(selected, 'done', !f.node.done);
        } else if (act === 'link') {
            linkFrom = selected;
            setLinkHint(true);
            markSelection();
        } else if (act === 'collapse') toggleCollapse(selected);
    });

    /* ── detail panel ── */
    on('mmNote', 'input', (e) => {
        const f = Model.find(doc, selected);
        if (!f) return;
        f.node.note = e.target.value.trim() ? e.target.value : undefined;
        if (!f.node.note) delete f.node.note;
        persist();
        clearTimeout(textTimer);
        textTimer = setTimeout(() => { draw(); syncText(); }, 260);
    });
    on('mmUrl', 'input', (e) => {
        const f = Model.find(doc, selected);
        if (!f) return;
        const v = e.target.value.trim();
        if (v) f.node.url = v; else delete f.node.url;
        persist();
        clearTimeout(textTimer);
        textTimer = setTimeout(() => { draw(); syncText(); }, 260);
    });
    on('mmDetailClose', 'click', closeDetail);
    const detail = $('mmDetail');
    if (detail) detail.addEventListener('click', (e) => {
        const sw = e.target.closest('.mm-swatch');
        if (sw) {
            const f = Model.find(doc, selected);
            const val = sw.getAttribute('data-colour');
            setFlag(selected, 'colour', f && f.node.colour === val ? null : val);
            fillDetail();
            return;
        }
        const ic = e.target.closest('.mm-icon-pick');
        if (ic) {
            const f = Model.find(doc, selected);
            const val = ic.getAttribute('data-icon');
            setFlag(selected, 'icon', f && f.node.icon === val ? null : val);
            fillDetail();
        }
        const unlink = e.target.closest('[data-act="unlink"]');
        if (unlink) {
            history.push(null);
            if (Model.unlinkNode(doc, selected)) afterChange({ keepText: true });
        }
    });

    /* ── library ── */
    const lib = $('mmLibList');
    if (lib) lib.addEventListener('click', (e) => {
        const open = e.target.closest('[data-open]');
        if (open) { openMap(open.getAttribute('data-open')); return; }
        const dup = e.target.closest('[data-dup]');
        if (dup) {
            const copy = Store.duplicate(dup.getAttribute('data-dup'));
            if (copy) openMap(copy.id);
            return;
        }
        const del = e.target.closest('[data-del]');
        if (del) {
            const id = del.getAttribute('data-del');
            Store.remove(id);
            if (id === doc.id) {
                const next = Store.opening();
                if (next) { doc = next; selected = doc.root.id; }
                else newMap();
                draw(); syncText();
            }
            refreshLibrary();
        }
    });
    on('mmNew', 'click', () => newMap());
    on('mmOpenFile', 'click', () => { const i = $('mmFile'); if (i) i.click(); });
    on('mmFile', 'change', (e) => {
        const f = e.target.files && e.target.files[0];
        if (f) openFile(f);
        e.target.value = '';
    });
    on('mmCollapseAll', 'click', () => { history.push(null); Model.setAllCollapsed(doc, true); afterChange({ keepText: true }); });
    on('mmExpandAll', 'click', () => { history.push(null); Model.setAllCollapsed(doc, false); afterChange({ keepText: true }); });

    /* ── exports ── */
    document.querySelectorAll('[data-export]').forEach((b) => {
        b.addEventListener('click', () => doExport(b.getAttribute('data-export')));
    });
    document.querySelectorAll('[data-org-export]').forEach((b) => {
        b.addEventListener('click', () => doOrgExport(b.getAttribute('data-org-export'), b));
    });

    /* ── organisation chart ── */
    document.querySelectorAll('[data-org-tpl]').forEach((b) => b.addEventListener('click', () => {
        orgTemplate(b.getAttribute('data-org-tpl'));
    }));
    on('mmOrgConvert', 'click', () => { setLayout('org'); closePops(); });
    on('mmOrgToMap', 'click', () => { setLayout('map'); closePops(); });
    on('mmOrgPasteBtn', 'click', () => {
        const box = $('mmOrgPaste');
        if (!box) return;
        box.hidden = !box.hidden;
        const b = $('mmOrgPasteBtn');
        if (b) b.setAttribute('aria-expanded', box.hidden ? 'false' : 'true');
        if (!box.hidden) { const ta = $('mmOrgPasteTa'); if (ta) ta.focus(); }
    });
    on('mmOrgPasteGo', 'click', () => { const ta = $('mmOrgPasteTa'); orgImportText(ta ? ta.value : ''); });
    on('mmOrgDemo', 'click', () => {
        const ta = $('mmOrgPasteTa');
        const sample = t('org_demo_table', '');
        if (ta) ta.value = sample;
        orgImportText(sample).then((okk) => { if (okk) ae('org_demo', {}); });
    });
    on('mmOrgTplXlsx', 'click', (e) => orgTemplateXlsx(e.currentTarget));
    const orgZone = $('mmOrgImport');
    if (orgZone) {
        const take = (f) => { if (f) orgImportFile(f); };
        if (window.__bindUploaderFile) window.__bindUploaderFile(orgZone, take);
        else orgZone.addEventListener('tool-uploader:file', (e) => take(e.detail && (e.detail.file || (e.detail.files && e.detail.files[0]))));
    }
    on('mmOrgName', 'input', (e) => setOrgOption('name', e.target.value));
    on('mmOrgPhotos', 'change', (e) => setOrgOption('photos', e.target.checked));
    on('mmOrgCount', 'change', (e) => setOrgOption('count', e.target.checked));
    on('mmOrgCompact', 'change', (e) => setOrgOption('compact', e.target.checked));
    on('mmOrgApOn', 'change', (e) => setOrgOption('approve.on', e.target.checked));
    on('mmOrgApPos', 'input', (e) => setOrgOption('approve.pos', e.target.value));
    on('mmOrgApFio', 'input', (e) => setOrgOption('approve.fio', e.target.value));
    on('mmOrgApDate', 'change', (e) => setOrgOption('approve.date', e.target.value));
    const depts = $('mmOrgDepts');
    if (depts) depts.addEventListener('toggle', () => { if (depts.open) { renderDeptTable(); ae('org_depts', {}); } });

    /* ── the card panel (org fields of the selected card) ── */
    const orgField = (id, ev, fn) => on(id, ev, (e) => {
        const f = Model.find(doc, selected);
        if (!f) return;
        fn(f.node, e.target, f);
    });
    orgField('mmOrgPerson', 'input', (n, el) => {
        history.push('flag:person:' + n.id);
        const v = el.value.trim();
        if (v) { n.person = v; delete n.vacant; } else delete n.person;
        persist(); clearTimeout(textTimer); textTimer = setTimeout(() => { draw(); syncText(); fillDetail(); }, 220);
    });
    orgField('mmOrgDept', 'input', (n, el) => {
        history.push('flag:dept:' + n.id);
        const v = el.value.trim();
        if (v) n.dept = v; else delete n.dept;
        persist(); clearTimeout(textTimer); textTimer = setTimeout(() => { draw(); syncText(); }, 220);
    });
    orgField('mmOrgStaffIn', 'change', (n, el) => {
        const v = Model.staffOf(el.value);
        setFlag(n.id, 'staff', v && v !== 1 ? v : null);
        fillDetail();
    });
    orgField('mmOrgVacant', 'change', (n, el) => {
        history.push(null);
        if (el.checked) { n.vacant = true; delete n.person; delete n.photo; } else delete n.vacant;
        afterChange(); fillDetail();
    });
    orgField('mmOrgAssistant', 'change', (n, el) => { setFlag(n.id, 'assistant', el.checked ? true : null); fillDetail(); });
    orgField('mmOrgUnit', 'change', (n, el) => { setFlag(n.id, 'kind', el.checked ? 'unit' : null); fillDetail(); });
    on('mmOrgPhotoBtn', 'click', () => { const i = $('mmOrgPhotoFile'); if (i) i.click(); });
    on('mmOrgPhotoFile', 'change', (e) => { const f = e.target.files && e.target.files[0]; if (f) setPhoto(f); e.target.value = ''; });
    on('mmOrgPhotoDel', 'click', () => { if (selected) { setFlag(selected, 'photo', null); fillDetail(); } });

    /* ── assistant ── */
    on('mmAiGo', 'click', () => askAi('map'));
    on('mmAiExpand', 'click', () => askAi('expand'));

    history.onChange((s) => {
        const u = $('mmUndo'), r = $('mmRedo');
        if (u) u.disabled = !s.canUndo;
        if (r) r.disabled = !s.canRedo;
    });

    // The fitted drawing depends on the stage's size, so a window (or sidebar)
    // resize has to re-measure it. ResizeObserver watches the ELEMENT, which is
    // what actually changed when the outline pane is toggled.
    if (window.ResizeObserver && stageEl) {
        let rt = null;
        new ResizeObserver(() => {
            clearTimeout(rt);
            rt = setTimeout(() => {
                // the automatic view was measured on the old box (the editor
                // goes wide AFTER the opening view): measure it again
                if (viewAuto === 'open') openView();
                else if (viewAuto === 'fit') fit();
                positionNodeBar();
            }, 120);
        }).observe(stageEl);
    }

    // Exposed for the browser test, which drives the same entry points a person
    // does rather than reaching into private state.
    window.__mm = {
        get doc() { return Model.plain(doc); },
        get laid() { return laid; },
        get selected() { return selected; },
        get view() { return { ...view }; },
        select, addChild, addSibling, removeNode, toggleCollapse,
        reorder, reparent, setFlag,
        promote: (id) => { history.push(null); if (Model.promote(doc, id)) afterChange(); },
        demote: (id) => { history.push(null); if (Model.demote(doc, id)) afterChange(); },
        crossLink: (a, b) => { history.push(null); Model.linkNodes(doc, a, b, ''); afterChange({ keepText: true }); },
        importOutline: (text) => {
            const parsed = Outline.parse(text, { rootText: t('starter_root', 'Моя карта') });
            if (!parsed) return false;
            newMap(Model.adopt({ root: parsed }));
            return true;
        },
        setText: (text) => { const ta = $('mmText'); if (ta) { ta.value = text; onTextInput(); } },
        text: () => Outline.stringify(doc.root, textOpts()),
        markdown: () => Exports.markdown(doc),
        opml: () => Exports.opml(doc),
        freemind: () => Exports.freemind(doc),
        csv: () => Exports.csv(doc),
        shareEncode: () => Share.encode(doc),
        shareDecode: (p) => Share.decode(p),
        exportPng: (side) => Exports.renderPng(doc, layoutNow(), side || pngLongSide()),
        exportSvgString: () => Exports.svgText(doc, layoutNow()),
        search: runSearch, clearSearch, jumpToHit,
        setView, setLayout,
        orgTemplate, orgImportText, setOrgOption, doOrgExport,
        orgSummary: () => Org.summary(doc.root),
        orgDeptTable: () => Org.deptTable(doc.root),
        orgKey: () => Org.docKey(doc),
        setTheme: (v) => { doc.theme = v; persist(); draw(); },
        setShape: (v) => { doc.shape = v; persist(); draw(); },
        library: () => Store.list(),
        openMap, newMap,
        present: startPresent,
        stopPresent: () => presenting && presenting.stop(),
        draw, fit, openView,
        undo: () => history.undo(), redo: () => history.redo(),
        count: () => Model.countNodes(doc),
        stats: () => Model.stats(doc),
    };
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', main);
else main();
